import type { ReactNode } from 'react';
import type { ToolUseItem } from '../../../contracts/harness.types';
import {
  readString,
  readToolResultRecord,
} from '../../../contracts/projects/harness/toolResult';
import {
  isEventTriggerRoutineTool,
  isOpenableRoutineTool,
  isScheduleRoutineTool,
} from '../../../contracts/workflows/lib/routineToolNames';
import type { Artifact } from '../../../data/projects/artifacts/state';
import type { RoutineOutcomeAction } from '../../inline-approval/routine-outcome-row';
import { WorkflowCreatedCard } from '../../workflows/components/WorkflowCreatedCard';

import { toBlockStatus } from '../../../data/projects/timeline/genericToolUseStatus';

const FALLBACK_ROUTINE_TITLE = 'Routine';

/**
 * `resolveArtifact` is the app's artifact deriver: the routine id it reads
 * decides whether the card opens the routine.
 */
export const renderWorkflowToolUse = (
  item: ToolUseItem,
  onOpenArtifact: (artifact: Artifact) => void,
  resolveArtifact: (item: ToolUseItem) => Artifact | undefined
): ReactNode | null => {
  if (!isOpenableRoutineTool(item.name)) return null;
  // Scheduling and trigger writes attach to a routine a create/update tool
  // already drew a WorkflowCreatedCard for — draw nothing here so the call
  // falls through to its `toolPresentation.ts` status line ("Scheduled
  // routine" / "Set routine triggers") instead of a second card.
  if (isScheduleRoutineTool(item.name) || isEventTriggerRoutineTool(item.name)) {
    return null;
  }

  const status = toBlockStatus(item.status);
  if (status !== 'success') return null;

  const title =
    readString(readToolResultRecord(item), 'name') ??
    readString(item.input, 'name') ??
    FALLBACK_ROUTINE_TITLE;
  const artifact = resolveArtifact(item);
  const action: RoutineOutcomeAction =
    item.name === 'update_routine' || item.name === 'update_workflow'
      ? 'updated'
      : 'created';

  if (artifact?.type === 'workflow') {
    return (
      <WorkflowCreatedCard
        title={title}
        action={action}
        onOpen={() => onOpenArtifact({ ...artifact, openInEdit: true })}
      />
    );
  }

  // Still show confirmation when workflow_id couldn't be derived (e.g. legacy
  // plain-text tool results) — just without an open action.
  return <WorkflowCreatedCard title={title} action={action} />;
};
